import { PhotoApi } from '@/api/photo-api'

import { defineStore } from 'pinia'

import {
  STATUS_LOAD,
  STATUS_DELETE_API,
  STATUS_UPDATE_API,
  STATUS_API_CALL,
  PHOTO_NUM_ITEMS_PAGE_INIT,
  PHOTO_FILTER_ORDERS,
  PHOTO_NUM_ITEMS_ADMIN,
  MULTIPLE_TYPE,
  FieldValues,
} from '@/constants'

interface State {
  offset: number
  limit: number
  photos: Array<Record<string, any>>
  axiosCurrentRequestId: string
  api: PhotoApi
  status: STATUS_LOAD
  statusDelete: STATUS_DELETE_API
  statusUpdate: STATUS_UPDATE_API
  indexSelected: number
  searchText: string | null
  searchType: number | null
  moreNextAvailable: boolean
  homePositions: Record<number, Array<boolean>>
  homePositionMaxPageWithPhoto: number

  filterOrder: number
  filterDates: Record<string, string>

  deleteModalTarget: string

  idForValidation: number

  isValidationHistoricChecked: boolean

  isMultipleEdition: boolean
  photoIdsMultipleEdition: Array<number>
  statusMultiplePhotos: STATUS_API_CALL
  multipleType: MULTIPLE_TYPE
  multipleUpdateItem: Record<string, any>
  multipleFields: Array<FieldValues>
  // Search Manual
}

type CallbackGetPhotoAdmin = (data: Array<Record<string, any>>) => void

export const usePhotoStore = defineStore('photo', {
  // convert to a function
  state: (): State => ({
    offset: 0,
    limit: PHOTO_NUM_ITEMS_PAGE_INIT,
    photos: [],
    axiosCurrentRequestId: '',
    api: new PhotoApi(),
    status: STATUS_LOAD.IDLE,
    statusDelete: STATUS_DELETE_API.IDLE,
    statusUpdate: STATUS_UPDATE_API.IDLE,
    indexSelected: -1,
    searchText: null,
    searchType: null,
    moreNextAvailable: true,
    homePositions: {},
    homePositionMaxPageWithPhoto: -1,

    filterOrder: PHOTO_FILTER_ORDERS[0].VALUE as number,
    filterDates: {},

    deleteModalTarget: '',

    idForValidation: -1,

    isValidationHistoricChecked: false,

    isMultipleEdition: false,
    photoIdsMultipleEdition: [],
    statusMultiplePhotos: STATUS_API_CALL.IDLE,
    multipleType: MULTIPLE_TYPE.NONE,
    multipleUpdateItem: {},
    multipleFields: [],
  }),

  getters: {
    getLimit: (state) => {
      console.log('getLimit', state.limit)
      return state.limit
    },

    getPhotoFromSelectedIndex: (state) => {
      return state.photos[state.indexSelected]
    },

    getIsValidationStatusChecked: (state) => {
      return state.isValidationHistoricChecked
    },

    getStatus: (state) => {
      return state.status
    },

    getStatusUpdate: (state) => {
      return state.statusUpdate
    },

    getSearchText: (state) => {
      return state.searchText
    },

    getDeleteModalTarget: (state) => {
      return state.deleteModalTarget
    },

    getIsMultipleEdition: (state) => {
      return state.isMultipleEdition
    },

    getHasMultipleEditionItems: (state) => {
      return state.photoIdsMultipleEdition.length > 0
    },

    getMultipleEditionItems: (state) => {
      return state.photoIdsMultipleEdition
    },

    getStatusMultipleApiCall: (state) => {
      return state.statusMultiplePhotos
    },

    isMultipleApiCallRunning: (state) => {
      return state.statusMultiplePhotos === STATUS_API_CALL.RUNNING
    },

    getMultipleType: (state) => {
      return state.multipleType
    },
  },

  actions: {
    // no context as first argument, use `this` instead
    async deletePhoto(item: Record<string, any>) {
      console.log('deletePhoto')

      try {
        this.statusDelete = STATUS_DELETE_API.DELETING

        item.deleting = true

        const photo: Record<string, any> = this.photos.find(
          (elt) => elt.id === item.id
        ) as Record<string, any>
        photo.deleting = true

        const response: any = await this.api.delete(item.id)
        console.log('data', response)

        this.photos = this.photos.filter((elt) => elt.id !== item.id)

        this.statusDelete = STATUS_DELETE_API.SUCCESS
      } catch (error) {
        console.error(error)

        this.statusDelete = STATUS_DELETE_API.FAILED
      }
    },

    async updatePhoto(params: Record<string, any>) {
      console.log('updatePhoto', params)

      const photo: Record<string, any> = this.photos.find(
        (item) => item.id === params.id
      ) as Record<string, any>

      try {
        this.statusUpdate = STATUS_UPDATE_API.UPDATING

        photo.updating = true
        params.updating = true

        const response: any = await this.api.update(params.id, params)
        console.log('data', response)

        const index: number = this.photos.findIndex(
          (item) => item.id === photo.id
        )
        this.photos[index] = response.data

        this.statusUpdate = STATUS_UPDATE_API.SUCCESS
      } catch (error) {
        console.error(error)

        this.statusUpdate = STATUS_UPDATE_API.FAILED
      } finally {
        photo.updating = false
        params.updating = false
      }
    },

    // *****************************
    //       Admin / Validation
    // *****************************
    setFilterDates(dates: Record<string, string>) {
      this.filterDates = dates

      this.offset = 0
      // Automatically refresh the list of carts
      this.getPhotosValidation()
    },

    // no context as first argument, use `this` instead
    async getPhotosValidation() {
      console.log('getPhotosValidation')

      this.photos = []
      this.offset = 0

      this.getPhotosValidationBase(this.getPhotoValidationNextCallback)
    },

    async getPhotosValidationPrevious() {
      console.log('getPhotosValidationPrevious')

      this.offset = this.offset - PHOTO_NUM_ITEMS_ADMIN

      this.getPhotosValidationBase(this.getPhotoValidationPreviousCallback)
    },

    async getPhotosValidationNext() {
      console.log('getPhotosValidationNext')

      this.offset = this.offset + PHOTO_NUM_ITEMS_ADMIN

      this.getPhotosValidationBase(this.getPhotoValidationNextCallback)
    },

    // no context as first argument, use `this` instead
    async getPhotosValidationBase(
      callback: CallbackGetPhotoAdmin | null = null
    ) {
      console.log('getPhotosValidationBase')

      try {
        this.status = STATUS_LOAD.LOADING

        const payload: Record<string, string | number> =
          this.getPhotoValidationParameters()

        const response: any = await this.api.getPhotoValidation(payload)
        console.log('data', response)

        if (response.data.length > 0) {
          this.photos = response.data
        }

        if (callback) {
          callback(response.data)
        }
        this.status = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.status = STATUS_LOAD.FAILED
      }
    },

    getPhotoValidationPreviousCallback() {
      console.log('getPhotoValidationPreviousCallback')

      this.moreNextAvailable = true
    },

    getPhotoValidationNextCallback(data: Array<Record<string, any>>) {
      console.log('getPhotoValidationNextCallback')

      if (data.length === 0 || data.length < PHOTO_NUM_ITEMS_ADMIN) {
        this.moreNextAvailable = false
      } else {
        this.moreNextAvailable = true
      }
    },

    getPhotoValidationParameters() {
      console.log('getPhotoValidationParameters')

      let payload: Record<string, string | number> = {
        offset: this.offset,
        limit: PHOTO_NUM_ITEMS_ADMIN,
        order: this.filterOrder,
        status: this.isValidationHistoricChecked
          ? 'ACTIVE'
          : 'PENDING_VALIDATION',
      }

      // Add Dates
      payload = { ...payload, ...this.filterDates }

      return payload
    },

    // no context as first argument, use `this` instead
    async setIndex(index: number) {
      console.log('setIndex', index)

      this.indexSelected = index
    },

    setIndexAsNext() {
      console.log('setIndexAsNext')

      this.indexSelected = this.indexSelected + 1

      if (this.indexSelected === this.photos.length) {
        this.indexSelected = 0
      }
    },

    // no context as first argument, use `this` instead
    resetPhotos() {
      console.log('resetPhotos')

      this.photos = []
    },

    setIdForValidation(id: number) {
      console.log('setIdForValidation', id)

      this.idForValidation = id
    },

    // ********************************
    //         Multiple Item
    // ********************************
    setIsMultipleEdition(value: boolean) {
      this.isMultipleEdition = value

      if (!value) {
        this.removeAllPhotoIdToMultipleEdition()
      }
    },

    setMultipleUpdateItem(item: Record<string, any>) {
      this.multipleUpdateItem = item
    },

    addPhotoIdToMultipleEdition(id: number) {
      this.photoIdsMultipleEdition.push(id)
    },

    removePhotoIdToMultipleEdition(id: number) {
      this.photoIdsMultipleEdition = this.photoIdsMultipleEdition.filter(
        (item) => item !== id
      )
    },

    removeAllPhotoIdToMultipleEdition() {
      this.photoIdsMultipleEdition = []
    },

    setStatusMultipleApiCall(status: STATUS_API_CALL) {
      this.statusMultiplePhotos = status
    },

    async multipleDeletePhoto() {
      console.log(
        'multipleDeletePhoto this.photoIdsMultipleEdition',
        this.photoIdsMultipleEdition
      )

      // Set the individual photo flag to true, so it stops the user from being to clikc on photo individually
      this.photos.forEach((item) => {
        if (this.photoIdsMultipleEdition.includes(item.id)) {
          item.deleting = true
        }
      })

      this.statusMultiplePhotos = STATUS_API_CALL.RUNNING

      try {
        await this.api.multipleDelete({
          list_ids: this.photoIdsMultipleEdition,
        })

        this.photos = this.photos.filter(
          (item) => !this.photoIdsMultipleEdition.includes(item.id)
        )

        this.removeAllPhotoIdToMultipleEdition()

        this.statusMultiplePhotos = STATUS_API_CALL.SUCCESS
      } catch (error) {
        console.error(error)
        this.statusMultiplePhotos = STATUS_API_CALL.FAILED
      }
    },

    async multipleUpdatePhoto() {
      console.log('multipleUpdatePhoto', this.multipleFields)

      // Set the individual photo flag to true, so it stops the user from being to clikc on photo individually
      this.photos.forEach((item) => {
        if (this.photoIdsMultipleEdition.includes(item.id)) {
          item.updating = true
        }
      })

      this.statusMultiplePhotos = STATUS_API_CALL.RUNNING

      try {
        const response: any = await this.api.multipleUpdate({
          list_ids: this.photoIdsMultipleEdition,
          fields: this.multipleFields,
        })
        console.log('data', response)

        console.log(
          'multipleUpdatePhoto this.photoIdsMultipleEdition',
          this.photoIdsMultipleEdition
        )
        this.photos.forEach((item: Record<string, any>) => {
          // Update the photo
          if (this.photoIdsMultipleEdition.includes(item.id)) {
            console.log('multipleUpdatePhoto UPDATE PHOTO')

            item.updating = false

            this.multipleFields.forEach((field) => {
              item.iptc[field.backend_key] = field.value
            })
          }
        })

        this.statusMultiplePhotos = STATUS_API_CALL.SUCCESS
      } catch (error) {
        console.error(error)
        this.statusMultiplePhotos = STATUS_API_CALL.FAILED
      }
    },

    async multipleValidationPhoto() {
      console.log(
        'multipleValidationPhoto this.photoIdsMultipleEdition',
        this.photoIdsMultipleEdition
      )

      // Set the individual photo flag to true, so it stops the user from being to clikc on photo individually
      this.photos.forEach((item) => {
        if (this.photoIdsMultipleEdition.includes(item.id)) {
          item.updating = true
        }
      })

      this.statusMultiplePhotos = STATUS_API_CALL.RUNNING

      try {
        await this.api.multipleValidation({
          list_ids: this.photoIdsMultipleEdition,
        })

        this.photos = this.photos.filter(
          (item) => !this.photoIdsMultipleEdition.includes(item.id)
        )

        this.removeAllPhotoIdToMultipleEdition()

        this.statusMultiplePhotos = STATUS_API_CALL.SUCCESS
      } catch (error) {
        console.error(error)
        this.statusMultiplePhotos = STATUS_API_CALL.FAILED
      }
    },

    setMultipleType(type: MULTIPLE_TYPE) {
      this.multipleType = type
    },

    setMultipleFields(fields: Array<FieldValues>) {
      this.multipleFields = fields
    },

    addAllItemsToMultipleSelection() {
      console.log('addAllItemsToMultipleSelection')

      this.photoIdsMultipleEdition = []
      this.photos.forEach((item) => {
        if (!item.is_doublon) {
          this.photoIdsMultipleEdition.push(item.id)
        }
      })

      console.log(
        'addAllItemsToMultipleSelection this.photoIdsMultipleEdition',
        this.photoIdsMultipleEdition
      )
    },
  },
})

export type PhotoStore = ReturnType<typeof usePhotoStore>;